import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { connect } from 'react-redux'
import { isMoment } from 'moment'
import { debounce, values } from 'lodash'
import { Button, DatePicker, Divider, Input, InputNumber, message, Switch, Tag } from 'antd'
import { faCheck, faClinicMedical, faExclamationTriangle, faLink, faUserGraduate } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

import SchoolsSelector from '../../../Components/shared/Selectors/School/SchoolsSelector'
import InstitutionsSelector from '../../../Components/shared/Selectors/Institution/InstitutionsSelector'
import EntityOptionLabel from '../../../Components/shared/Selectors/EntityOptionLabel'
import SectorSelector from '../../../Components/shared/Selectors/SectorSelector'
import { isValid } from '../../../utils/validators'
import { EUROPEAN_DATE, InternshipStates, InternshipStatesAsString, QuotasPrecisions } from '../../../utils/constants'
import SchoolSectionSelector from '../../../Components/shared/Selectors/SchoolSectionSelector'
import EntitiesSelector from '../../../Components/shared/Selectors/EntitiesSelector'
import ActTypesSelector from '../../../Components/shared/Selectors/ActTypesSelector'
import { isSchoolUser } from '../../../utils/roles'
import { selectUser } from '../../../reducers/UserReducer'
import IconButton from '../../../Components/antd/Buttons/IconButton'
import { canUserEditProperty, Internship, InternshipProperties } from '../../../utils/entities/internship'
import { getSchoolSchoolSections } from '../../../utils/api/school'
import { getActTypes, getSchoolSectionSectionStudents } from '../../../utils/api/schoolSection'
import { getAllStudents, getStudentInternships, SerializationContexts } from '../../../utils/api/student'
import { getInternshipsQuotas } from '../../../utils/api/quota'
import { API_DATE } from '../../../utils/momentjs'
import { retrieveMetadata } from '../../../utils/http'
import StudentsSelector from '../../../Components/shared/Selectors/Student/StudentsSelector'
import { getInstitutionSectors } from '../../../utils/api/institution'
import { selectTranslate } from '../../../reducers/LocaleReducer/LocalReducer'
import { formatAsArray, getIdOrNull, isNonEmptyArray, isObject } from '../../../utils'
import { Modes } from './AutonomousInternshipFormModal'
import InternshipDurationItem from '../../../Components/shared/Forms/InternshipDurationItem'
import FormItem from '../../../Components/shared/Forms/FormItem'
import { GlobalContext } from '../../GlobalProvider'
import { toWebUrl } from '../../../utils/httpTools'

import './assets/autonomous-internship-form.scss'

const BULK_QUANTITY = { MIN: 2, MAX: 20 }

const OVERLAP_INTERNSHIPS_CONTEXTS = ['sector', 'institution']

/** A canceled or a refused internship no longer occupies the student, it cannot conflict with a new one. */
const OVERLAP_IGNORED_STATES = [InternshipStates.CANCELED, InternshipStates.REFUSED]
const OVERLAP_IGNORED_STATES_AS_STRING = [InternshipStatesAsString.CANCELED, InternshipStatesAsString.REFUSED]

const isIgnoredForOverlap = internship => OVERLAP_IGNORED_STATES.includes(internship.state) || OVERLAP_IGNORED_STATES_AS_STRING.includes(internship.state)

const { START_DATE, END_DATE, SCHOOL, SECTION, INSTITUTION, SECTOR, STUDENT, EXTERNAL_EVALUATION_LINK } = InternshipProperties

const FormSection = ({ icon, title, children }) => (
  <div className='form-section'>
    <div className='form-section-header'>
      <span className='form-section-icon'>
        <FontAwesomeIcon icon={icon} />
      </span>
      <span className='form-section-title'>{title}</span>
    </div>
    <div className='form-section-row'>{children}</div>
  </div>
)

message.config({
  top: 20,
  duration: 3,
  maxCount: 1,
  placement: 'topRight'
})

const mapStateToProps = state => ({ user: selectUser(state), t: selectTranslate(state) })

const AutonomousInternshipForm = ({ data, mode, sectors, user, validators, onSubmit, onCancel, t }) => {
  const { sectionToYearsBySection, isDraft } = useContext(GlobalContext)

  const [error, setError] = useState({ missingFields: [] })
  const [localData, setLocalData] = useState(data)
  const [sections, setSections] = useState({ data: [], loading: false })
  const [students, setStudents] = useState({ data: [], loading: false, page: 1, hasMore: false })
  const [studentSearch, setStudentSearch] = useState('')
  const [localSectors, setLocalSectors] = useState({ data: [], loading: false })
  const [bulkAdd, setBulkAdd] = useState(false)
  const [bulkQuantity, setBulkQuantity] = useState(BULK_QUANTITY.MIN)
  const [remainingPlaces, setRemainingPlaces] = useState(null)
  const [overlappingInternships, setOverlappingInternships] = useState([])
  const [actTypes, setActTypes] = useState([])

  const isSchoolSide = isSchoolUser(user)

  useEffect(() => {
    if (localData?.school) {
      setSections({ data: [], loading: true })
      getSchoolSchoolSections(user, { id: getIdOrNull(localData.school) }).then(({ data }) => {
        const sections = data[getIdOrNull(localData.school)]

        setSections({ data: sections.filter(s => !!s.sectionToYear), loading: false })
      })
    }
  }, [user, localData?.school])

  const fetchStudentsPage = useCallback((schoolId, page, append) => {
    getAllStudents(user, {
      paginate: true,
      page,
      pageSize: 50,
      search: studentSearch,
      filters: { schools: [schoolId] },
      contexts: [SerializationContexts.SECTION]
    }).then(({ data, meta }) => {
      const totalPages = retrieveMetadata(meta).totalPages ?? page

      setStudents(prev => ({
        data: append ? [...prev.data, ...(data ?? [])] : (data ?? []),
        loading: false,
        page,
        hasMore: page < totalPages
      }))
    }).catch(() => {
      setStudents({ data: [], loading: false, page: 1, hasMore: false })
    })
  }, [user, studentSearch])

  useEffect(() => {
    const schoolId = getIdOrNull(localData?.school)
    const sectionId = getIdOrNull(localData?.section)

    if (!isSchoolSide || schoolId === null) {
      return
    }

    if (sectionId !== null) {
      setStudents({ data: [], loading: true, page: 1, hasMore: false })

      getSchoolSectionSectionStudents(user, { id: sectionId }).then(({ data }) => {
        setStudents({ data: data ?? [], loading: false, page: 1, hasMore: false })
      }).catch(() => {
        setStudents({ data: [], loading: false, page: 1, hasMore: false })
      })

      return
    }

    setStudents(prev => ({ ...prev, loading: true }))

    const fetch = debounce(() => fetchStudentsPage(schoolId, 1, false), 400)

    fetch()

    return () => fetch.cancel()
  }, [isSchoolSide, localData?.school, localData?.section, user, fetchStudentsPage])

  const handleStudentsPopupScroll = e => {
    const { target } = e
    const reachedBottom = target.scrollTop + target.clientHeight >= target.scrollHeight - 20
    const schoolId = getIdOrNull(localData?.school)

    if (reachedBottom && !students.loading && students.hasMore && schoolId !== null) {
      setStudents(prev => ({ ...prev, loading: true }))
      fetchStudentsPage(schoolId, students.page + 1, true)
    }
  }

  useEffect(() => {
    if (localData?.institution && isSchoolSide) {
      setLocalSectors({ data: [], loading: true })
      getInstitutionSectors(user, { id: getIdOrNull(localData.institution) }).then(({ data }) => {
        setLocalSectors({ data, loading: false })
      })
    }
  }, [user, localData?.institution])

  useEffect(() => {
    /** The route resolves the school and the year from the school section itself, so it wants its own id. */
    const schoolSectionId = getIdOrNull(localData?.section)
    const sectorId = getIdOrNull(localData?.sector)
    const { startDate, endDate } = localData ?? {}

    if (!isSchoolSide || schoolSectionId === null || sectorId === null || !isMoment(startDate) || !isMoment(endDate)) {
      setRemainingPlaces(null)
      return
    }

    let cancelled = false

    getInternshipsQuotas(user, undefined, {
      period: { startDate: startDate.format(API_DATE), endDate: endDate.format(API_DATE) },
      sector: sectorId,
      section: schoolSectionId,
      precision: QuotasPrecisions.YEAR
    }).then(({ data }) => {
      if (!cancelled) {
        /**
         * The simulated internship has no id, so it comes back under a single empty key : one entry per week of the period.
         * A week without any quota is null, which means unlimited and not full, hence the weeks kept are the counted ones only.
         */
        const places = isObject(data) ? Object.values(data).flat() : data
        const weeklyPlaces = isNonEmptyArray(places) ? places.filter(place => typeof place === 'number') : []

        setRemainingPlaces(isNonEmptyArray(weeklyPlaces) ? Math.min(...weeklyPlaces) : null)
      }
    }).catch(() => {
      if (!cancelled) {
        setRemainingPlaces(null)
      }
    })

    return () => {
      cancelled = true
    }
  }, [isSchoolSide, user, localData?.section, localData?.sector, localData?.startDate, localData?.endDate])

  useEffect(() => {
    const student = localData?.student
    const { startDate, endDate } = localData ?? {}

    if (!isSchoolSide || bulkAdd || getIdOrNull(student) === null || !isMoment(startDate) || !isMoment(endDate)) {
      setOverlappingInternships([])
      return
    }

    let cancelled = false

    getStudentInternships(user, student, { contexts: OVERLAP_INTERNSHIPS_CONTEXTS }).then(({ data }) => {
      if (!cancelled) {
        /** The same rule as the back end one : two internships touching by a single edge are not overlapping. */
        setOverlappingInternships(formatAsArray(data)
          .map(internship => new Internship(internship))
          .filter(internship => internship.id !== localData.id && !isIgnoredForOverlap(internship) && internship.startDate < endDate && internship.endDate > startDate)
        )
      }
    }).catch(() => {
      if (!cancelled) {
        setOverlappingInternships([])
      }
    })

    return () => {
      cancelled = true
    }
  }, [isSchoolSide, bulkAdd, user, localData?.id, localData?.student, localData?.startDate, localData?.endDate])

  const selectedSchoolSection = useMemo(() => sections.data.find(ss => ss.id === getIdOrNull(localData.section)) ?? null, [localData.section, sections.data])

  const actTypesSchoolSection = useMemo(() => {
    const internshipYearId = getIdOrNull(localData.internshipYear)

    if (!isSchoolSide || mode !== Modes.CREATE || !selectedSchoolSection?.actsMandatory || internshipYearId === null) {
      return null
    }

    return sections.data.find(ss => getIdOrNull(ss.sectionToYear) === internshipYearId) ?? null
  }, [isSchoolSide, mode, selectedSchoolSection, localData.internshipYear, sections.data])

  useEffect(() => {
    setActTypes([])
    setLocalData(prev => isNonEmptyArray(prev.actTypes) ? { ...prev, actTypes: [] } : prev)

    if (actTypesSchoolSection === null) {
      return
    }

    let cancelled = false

    getActTypes(user, actTypesSchoolSection).then(json => {
      if (!cancelled) {
        setActTypes(formatAsArray(json?.data).filter(actType => actType.hoursFormat))
      }
    })

    return () => {
      cancelled = true
    }
  }, [user, actTypesSchoolSection])

  const isExternalEvaluationLinkInvalid = useMemo(() => {
    const link = (localData.externalEvaluationLink ?? '').trim()

    return link !== '' && link !== (data.externalEvaluationLink ?? '').trim() && toWebUrl(link) === null
  }, [localData.externalEvaluationLink, data.externalEvaluationLink])

  const handleSubmit = (e) => {
    const errors = isValid(localData, values(validators))

    errors.keys = errors.keys.filter(key => key !== SECTION)

    if (isExternalEvaluationLinkInvalid) {
      errors.keys.push(EXTERNAL_EVALUATION_LINK)
    }

    if (localData.startDate > localData.endDate) {
      message.warning(t('internship_form.error_dates'))
    } else if (isExternalEvaluationLinkInvalid) {
      message.warning(t('internship_form.error_external_evaluation_link'))
    } else if (errors.keys.length === 0) {
      const asDraft = internship => {
        if (isDraft && mode === Modes.CREATE) {
          internship.isDraft = true
        }

        return internship
      }

      onSubmit(
        bulkAdd
          ? Array.from({ length: bulkQuantity }, () => asDraft(new Internship({ ...localData, student: null })))
          : asDraft(new Internship(localData)),
        formatAsArray(localData.actTypes)
      )
    }

    setError({
      ...error,
      title: t('internship_form.error_field'),
      visible: true,
      missingFields: errors.keys
    })
  }

  const getStyle = key => {
    const style = { width: '100%' }

    if (error.missingFields.includes(key)) {
      style.border = '2px solid red'
      style.borderRadius = '4px'
    }

    return style
  }

  const isDisabled = useCallback(property => !canUserEditProperty(user, localData, property), [user, localData])
  const fixedEntity = isSchoolSide ? localData.school : localData.institution

  const internshipYears = useMemo(() => {
    const baseSectionId = selectedSchoolSection?.sectionId ?? null

    return baseSectionId !== null ? (sectionToYearsBySection?.[baseSectionId] ?? []) : []
  }, [selectedSchoolSection, sectionToYearsBySection])

  return (
    <div className="autonomous-internship-form flex-column">
      {isObject(fixedEntity) && (
        <div className="fixed-entity-container">
          <Tag className="fixed-entity-tag">
            {t(isSchoolSide ? "common.school" : "common.institution")} :{" "}
            <EntityOptionLabel entity={fixedEntity} label={fixedEntity?.name} />
          </Tag>
        </div>
      )}

      {isSchoolSide && mode === Modes.CREATE && (
        <div className="bulk-add-row">
          <Switch
            size="small"
            checked={bulkAdd}
            onChange={(checked) => {
              setBulkAdd(checked);
              if (checked) {
                setLocalData((prev) => ({
                  ...prev,
                  student: null,
                  externalEvaluationLink: null,
                }));
              }
            }}
          />
          <span className="bulk-add-label">
            {t("internship_form.bulk_add")}
          </span>
          {bulkAdd && (
            <InputNumber
              size="small"
              className="bulk-add-quantity"
              min={BULK_QUANTITY.MIN}
              max={BULK_QUANTITY.MAX}
              value={bulkQuantity}
              onChange={setBulkQuantity}
            />
          )}
        </div>
      )}

      <div className="form-class">
        <FormSection icon={faClinicMedical} title={t('internship_form.location_and_period')}>
          {isSchoolSide && (
            <FormItem label="common.institution">
              <InstitutionsSelector
                multiple="default"
                values={
                  isObject(localData.institution) && localData.institution.name
                    ? localData.institution.name
                    : getIdOrNull(localData.institution)
                }
                onChange={(institution) =>
                  setLocalData({ ...localData, institution, sector: null })
                }
                disabled={isDisabled(INSTITUTION)}
                style={getStyle(INSTITUTION)}
              />
            </FormItem>
          )}
          <FormItem label="common.sector">
            <SectorSelector
              disabled={!localData.institution || isDisabled(SECTOR)}
              loading={isSchoolSide && localSectors.loading}
              sectors={isSchoolSide ? localSectors.data : sectors}
              values={getIdOrNull(localData.sector)}
              onChange={(sector) => setLocalData({ ...localData, sector })}
              style={{ ...getStyle(SECTOR) }}
            />
          </FormItem>
          <FormItem label="common.period">
            <DatePicker.RangePicker
              value={[localData.startDate, localData.endDate]}
              onChange={(dates) =>
                setLocalData({
                  ...localData,
                  startDate: dates?.[0] ?? null,
                  endDate: dates?.[1] ?? null,
                })
              }
              format={EUROPEAN_DATE}
              style={getStyle(START_DATE)}
              disabled={isDisabled(START_DATE) || isDisabled(END_DATE)}
            />
          </FormItem>
          <InternshipDurationItem
            compensation={localData.compensation}
            duration={localData.duration}
            onDurationChange={(duration) =>
              setLocalData({ ...localData, duration })
            }
            onCompensationChange={(compensation) =>
              setLocalData({ ...localData, compensation })
            }
            showIcon={false}
          />
        </FormSection>

        <FormSection icon={faUserGraduate} title={t("common.student")}>
          {!isSchoolSide && (
            <FormItem label="common.school">
              <SchoolsSelector
                multiple="default"
                values={
                  isObject(localData.school) && localData.school.name
                    ? localData.school.name
                    : getIdOrNull(localData.school)
                }
                onChange={(school) =>
                  setLocalData({ ...localData, school, section: null })
                }
                style={getStyle(SCHOOL)}
                disabled={isDisabled(SCHOOL)}
              />
            </FormItem>
          )}
          {isSchoolSide ? (
            <>
              <FormItem label="common.student">
                <StudentsSelector
                  students={students.data}
                  values={getIdOrNull(localData.student)}
                  onChange={(value, _, student) => {
                    const studentSection =
                      student?.sections?.find(
                        (s) =>
                          getIdOrNull(s.school) ===
                          getIdOrNull(localData.school),
                      ) ?? null;

                    setLocalData((prev) => ({
                      ...prev,
                      student,
                      section: studentSection?.id ?? prev.section,
                      internshipYear: studentSection
                        ? getIdOrNull(studentSection.sectionToYear)
                        : prev.internshipYear,
                    }));
                  }}
                  style={getStyle(STUDENT)}
                  loading={students.loading}
                  showSearch
                  onSearch={localData.section ? undefined : setStudentSearch}
                  onPopupScroll={
                    localData.section ? undefined : handleStudentsPopupScroll
                  }
                  disabled={bulkAdd}
                />
              </FormItem>
              <FormItem label="common.section">
                <SchoolSectionSelector
                  disabled={
                    sections.data.length === 0 ||
                    sections.loading ||
                    isDisabled(SECTION)
                  }
                  schoolSections={
                    localData.student
                      ? sections.data.filter(
                          (ss) => ss.id === getIdOrNull(localData.section),
                        )
                      : sections.data
                  }
                  values={getIdOrNull(localData.section)}
                  onChange={(section, _, entities) => {
                    const selectedEntity = Array.isArray(entities)
                      ? entities[0]
                      : entities;

                    setLocalData((prev) => ({
                      ...prev,
                      student: prev.section === section ? prev.student : null,
                      section,
                      internshipYear: section
                        ? getIdOrNull(selectedEntity?.sectionToYear)
                        : null,
                    }));
                  }}
                  style={getStyle(SECTION)}
                  loading={sections.loading}
                />
              </FormItem>
              <FormItem label="common.year">
                <EntitiesSelector
                  entityOptions={internshipYears.map((sty) => ({
                    label: sty.schoolYearName,
                    value: sty.id,
                    entity: sty,
                  }))}
                  disabled={
                    !localData.section ||
                    !internshipYears.length ||
                    isDisabled(SECTION)
                  }
                  loading={sections.loading}
                  multiple="default"
                  showSearch
                  values={getIdOrNull(localData.internshipYear)}
                  onChange={(value) =>
                    setLocalData((prev) => ({
                      ...prev,
                      internshipYear: getIdOrNull(value),
                    }))
                  }
                  style={getStyle(SECTION)}
                />
              </FormItem>
            </>
          ) : (
            <FormItem label="common.section_and_year">
              <SchoolSectionSelector
                disabled={
                  sections.data.length === 0 ||
                  sections.loading ||
                  isDisabled(SECTION)
                }
                schoolSections={sections.data}
                values={getIdOrNull(localData.section)}
                onChange={(section, _, entities) => {
                  const selectedEntity = Array.isArray(entities)
                    ? entities[0]
                    : entities;
                  setLocalData((prev) => ({
                    ...prev,
                    section,
                    internshipYear: selectedEntity?.sectionToYear ?? null,
                  }));
                }}
                style={getStyle(SECTION)}
                loading={sections.loading}
              />
            </FormItem>
          )}
        </FormSection>

        {isSchoolSide && (
          <FormSection icon={faLink} title={t('internship_form.additional_informations_optional')}>
            {isNonEmptyArray(actTypes) && (
              <FormItem className='form-item-full' label='internship_form.act_types'>
                <ActTypesSelector
                  actTypes={actTypes}
                  multiple='multiple'
                  values={formatAsArray(localData.actTypes)}
                  onChange={value => setLocalData({ ...localData, actTypes: formatAsArray(value) })}
                  style={{ width: '100%' }}
                />
              </FormItem>
            )}
            <FormItem className="form-item-full" label="internship_form.external_evaluation_link">
              <Input
                value={localData.externalEvaluationLink ?? ''}
                onChange={(e) =>
                  setLocalData({ ...localData, externalEvaluationLink: e.target.value })
                }
                disabled={bulkAdd}
                style={getStyle(EXTERNAL_EVALUATION_LINK)}
              />
            </FormItem>
          </FormSection>
        )}
      </div>

      {isNonEmptyArray(overlappingInternships) && (
        <div className="overlap-banner">
          <FontAwesomeIcon className="overlap-banner-icon" icon={faExclamationTriangle} />
          <div className="overlap-banner-body">
            <span className="overlap-banner-title">
              {t("This student is already doing an internship during this period.")}
            </span>
            <ul className="overlap-banner-list">
              {overlappingInternships.map((internship) => (
                <li key={internship.id}>
                  {internship.startDate.format(EUROPEAN_DATE)} - {internship.endDate.format(EUROPEAN_DATE)}
                  {isObject(internship.sector) && ` · ${internship.sector.name}`}
                  {isObject(internship.institution) && ` (${internship.institution.name})`}
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {isSchoolSide && remainingPlaces !== null && (
        <div className="quota-container">
          <div
            className={`quota-message ${
              remainingPlaces < 0
                ? "quota-overloaded"
                : remainingPlaces === 0
                  ? "quota-balanced"
                  : "quota-underloaded"
            }`}
          >
            {t(
              remainingPlaces < 0
                ? "internship_form.quota_excess"
                : "internship_form.quota_remaining",
              { count: Math.abs(remainingPlaces) },
            )}
          </div>
        </div>
      )}

      <div className="modal-footer">
        <div className="form-footer flex-row aln-ctr jst-end">
          <Button key="cancel" type="default" onClick={onCancel}>
            {" "}
            {t("common.cancel")}{" "}
          </Button>
          <IconButton
            key="save"
            type="primary"
            onClick={handleSubmit}
            icon={faCheck}
            text={t("common.save")}
          />
        </div>
      </div>
    </div>
  );
}

export default connect(mapStateToProps)(AutonomousInternshipForm)
